import { useEffect } from 'react'
import { toast } from 'sonner'
import { AlertTriangle, HelpCircle, RotateCw } from 'lucide-react'
import { ConnectError, Code } from '@connectrpc/connect'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { captureError } from '@/lib/sentry'
import { useRetryEmailSend } from '@/stores/emails.store'
import { EmailDeliveryStatus } from '@/gen/cortado/v1/emails_pb'

/**
 * Delivery states that represent a send that did not reach the recipient and
 * may be worth retrying. This must match what the backend actually honors:
 * `RetryOutboundEmailSend` (backend/pkg/services/email/outbox.go) only lets
 * the delivery workflow re-claim rows whose `delivery_status` is `queued` or
 * `failed` — every other visible-failure status (`BOUNCED`, `COMPLAINED`,
 * `SUPPRESSED`) is rejected with `ErrOutboundEmailNotRetryable` once the
 * workflow actually tries to claim it, even though the RPC call itself
 * returns success. Offering Retry on those statuses produces a false
 * "queued for retry" toast with no delivery attempt.
 *
 * `UNCERTAIN` must NEVER be added here. Gmail has no provider idempotency
 * key, so a send whose outcome is unconfirmed cannot be safely retried — a
 * retry risks a second copy landing in the customer's inbox. The check below
 * fails fast (at module load) if a future edit adds it anyway.
 */
const RETRYABLE_STATUSES = new Set([
  EmailDeliveryStatus.FAILED,
])

if (RETRYABLE_STATUSES.has(EmailDeliveryStatus.UNCERTAIN)) {
  throw new Error(
    'EmailDeliveryStatus.UNCERTAIN must never be retryable: Gmail sends have no idempotency key, so retrying an unconfirmed send risks a duplicate in the customer’s inbox.',
  )
}

const VISIBLE_FAILURE_STATUSES = new Set([
  EmailDeliveryStatus.FAILED,
  EmailDeliveryStatus.BOUNCED,
  EmailDeliveryStatus.COMPLAINED,
  EmailDeliveryStatus.SUPPRESSED,
])

/** Whether an outbound message's delivery status is worth surfacing as a failure. */
export function isVisibleDeliveryFailure(status: EmailDeliveryStatus): boolean {
  return VISIBLE_FAILURE_STATUSES.has(status)
}

/**
 * Whether an outbound message's delivery outcome is unconfirmed. Distinct
 * from `isVisibleDeliveryFailure`: we called Gmail and genuinely don't know
 * whether the customer received it. Not a failure (it may well have
 * delivered) and not a success (we can't confirm that either).
 */
export function isUncertainDelivery(status: EmailDeliveryStatus): boolean {
  return status === EmailDeliveryStatus.UNCERTAIN
}

interface FailedSendIndicatorProps {
  /** Id of the `Email` row this indicator describes. */
  emailId: string
  /** Organization the email belongs to. Falls back to the selected org when omitted. */
  organizationId?: string
  deliveryStatus: EmailDeliveryStatus
  bounceReason?: string
  /** Reason the send attempt itself failed (distinct from a delivery bounce). */
  lastSendError?: string
  /** Total send attempts recorded for this email. Backend populates this unconditionally. */
  sendAttempts?: number
  className?: string
}

/**
 * Compact badge + retry affordance for an outbound message that failed to
 * deliver, or whose delivery outcome is uncertain. Renders nothing for
 * statuses that are neither.
 */
export function FailedSendIndicator({
  emailId,
  organizationId,
  deliveryStatus,
  bounceReason,
  lastSendError,
  sendAttempts,
  className,
}: FailedSendIndicatorProps) {
  const { retry, mutation } = useRetryEmailSend()
  const isRetrying = mutation.isPending
  const isUncertain = isUncertainDelivery(deliveryStatus)
  const isVisible = VISIBLE_FAILURE_STATUSES.has(deliveryStatus) || isUncertain

  // send_attempts is NOT NULL DEFAULT 0 on the backend and populated on every
  // row — an undefined value here means the row wasn't populated correctly,
  // which is a bug worth surfacing rather than a legitimate "never attempted" state.
  useEffect(() => {
    if (isVisible && sendAttempts === undefined) {
      captureError(new Error('Outbound email is missing send_attempts'), {
        context: 'failed-send-retry',
        emailId,
      })
    }
  }, [isVisible, sendAttempts, emailId])

  if (!isVisible) {
    return null
  }

  // Never true for UNCERTAIN — enforced above at module load, not just here.
  const isRetryable = RETRYABLE_STATUSES.has(deliveryStatus)
  const label = failureLabel(deliveryStatus)
  const tooltip = failureTooltip(deliveryStatus, bounceReason, lastSendError)

  const handleRetry = async () => {
    try {
      await retry({ emailId, organizationId })
      toast.success('Email queued for retry')
    } catch (error) {
      if (error instanceof ConnectError && error.code === Code.NotFound) {
        // Email is gone, or belongs to another tenant — indistinguishable
        // (and should be) from the user's point of view.
        toast.error('Email not found', {
          description: 'This message could not be found. It may have been deleted.',
        })
        return
      }
      if (error instanceof ConnectError && error.code === Code.FailedPrecondition) {
        // Already sent, or not retryable — either way not a bug.
        toast.error('Cannot retry this message', {
          description: getErrorDescription(error) || 'This message has already been sent or is not retryable.',
        })
        return
      }
      // Internal (workflow start failed) and anything else: a genuine
      // failure worth reporting, and one the user can retry again.
      reportError(error, { feature: 'email_retry_send', kind: 'mutation' })
      captureError(error instanceof Error ? error : new Error(String(error)), {
        context: 'failed-send-retry',
      })
      toast.error('Failed to retry send', {
        description: getErrorDescription(error),
      })
    }
  }

  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Badge variant={isUncertain ? 'warning' : 'destructive'}>
                {isUncertain ? <HelpCircle className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                {label}
              </Badge>
            </span>
          </TooltipTrigger>
          {tooltip && <TooltipContent side="top">{tooltip}</TooltipContent>}
        </Tooltip>

        {sendAttempts !== undefined && (
          <span className="text-xs text-muted-foreground">
            {sendAttempts} {sendAttempts === 1 ? 'attempt' : 'attempts'}
          </span>
        )}

        {isRetryable && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-6 gap-1 px-2 text-xs"
            disabled={isRetrying}
            onClick={() => void handleRetry()}
          >
            <RotateCw className={isRetrying ? 'h-3 w-3 animate-spin' : 'h-3 w-3'} />
            {isRetrying ? 'Retrying...' : 'Retry'}
          </Button>
        )}
      </div>

      {/*
       * Kept visible rather than tucked into the hover tooltip: the risk of
       * sending a duplicate to the customer is important enough that the
       * user shouldn't have to discover it by hovering.
       */}
      {isUncertain && (
        <p className="mt-1 text-xs text-muted-foreground">
          Resending risks sending a duplicate — check with the recipient before resending.
        </p>
      )}
    </div>
  )
}

function failureLabel(status: EmailDeliveryStatus): string {
  switch (status) {
    case EmailDeliveryStatus.FAILED:
      return 'Failed to send'
    case EmailDeliveryStatus.BOUNCED:
      return 'Bounced'
    case EmailDeliveryStatus.COMPLAINED:
      return 'Reported as spam'
    case EmailDeliveryStatus.SUPPRESSED:
      return 'Suppressed'
    case EmailDeliveryStatus.UNCERTAIN:
      return 'May have been sent'
    default:
      return 'Delivery issue'
  }
}

function failureTooltip(
  status: EmailDeliveryStatus,
  bounceReason?: string,
  lastSendError?: string,
): string | null {
  switch (status) {
    case EmailDeliveryStatus.FAILED:
      return lastSendError?.trim() || 'This message failed to send. Retrying will queue it for delivery again.'
    case EmailDeliveryStatus.BOUNCED:
      return bounceReason?.trim() || 'The recipient mail server rejected this message.'
    case EmailDeliveryStatus.COMPLAINED:
      return 'The recipient reported this message as spam.'
    case EmailDeliveryStatus.SUPPRESSED:
      return 'This recipient is on the suppression list; the message was not sent and cannot be retried until the suppression is cleared.'
    case EmailDeliveryStatus.UNCERTAIN:
      return lastSendError?.trim() || 'We couldn’t confirm whether this message reached the recipient — it may have been delivered.'
    default:
      return null
  }
}
